import React, { useCallback, useEffect, useState } from "react";
import { BuyOptionCard } from "web/react/components/buy-option-card";
import { Button } from "web/react/emo/button";
import { RadioCardGroup } from "web/react/emo/radio-card-group";
import { Text } from "web/react/emo/text";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { useFetchRetailerInfo } from "web/react/hooks/use-fetch-retailer-info";
import {
    ProductPurchaseType,
    useInStockProductPageContext,
} from "web/react/pages/product/in-stock/in-stock-product-page/in-stock-product-page.context";
import {
    getBuyOptionDisplayPrice,
    getBuyOptionStickerType,
    getPromotion,
    getShippingInformation,
} from "web/react/pages/product/in-stock/in-stock-product-page/utils";
import analytics from "web/script/analytics/analytics";
import { gettext, ngettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import { ProductBuyOptionsSerializer, ProductSerializer } from "web/types/serializers";
import * as styles from "./price-comparison-cards.css";

interface PriceComparisonCardsProps {
    product: ProductSerializer;
    buyOptions: ProductBuyOptionsSerializer[];
}

function PriceComparisonCardsMobile({
    product,
    buyOptions,
}: PriceComparisonCardsProps): JSX.Element {
    const {
        activeBuyOption,
        comparison: { closeCompareModal },
        productPurchaseType,
        openRetailerInfoModal,
        activeProduct: { selectedSizeOption },
    } = useInStockProductPageContext();

    const { fetchRetailerInfo } = useFetchRetailerInfo();
    const isDeepAffiliatePhase1Enabled = environment.getFeature(
        "deep_affiliate_experiment_phase_1"
    );
    const shouldShowViewSellerDetails = !isDeepAffiliatePhase1Enabled;

    const isCheckout = productPurchaseType === ProductPurchaseType.CHECKOUT;

    const [selectedBuyOption, setSelectedBuyOption] = useState(activeBuyOption.buyOption);

    const selectBuyOption = useCallback(() => {
        analytics.event({
            category: "price_comparison",
            action: "selected",
        });
        activeBuyOption.selectBuyOption(selectedBuyOption);
        closeCompareModal();
    }, [activeBuyOption, selectedBuyOption, closeCompareModal]);

    const onItemSelect = useCallback(
        (buyOption: ProductBuyOptionsSerializer) => setSelectedBuyOption(buyOption),
        []
    );

    const handleOpenRetailerModal = useCallback(
        async (retailerSlug: string): Promise<void> => {
            openRetailerInfoModal();
            await fetchRetailerInfo(retailerSlug);
        },
        [openRetailerInfoModal, fetchRetailerInfo]
    );

    return (
        <form className={styles.mobileWrapper}>
            <div className={styles.mobileCardsHeading}>
                <Text as="p" textStyle={"body-s"}>
                    {gettext("price_comparison.price_options")}
                </Text>
            </div>
            <div className={styles.mobileScrollArea}>
                <div className={styles.mobileCardsWrapper}>
                    <RadioCardGroup stacked={false} gap="xxs">
                        {buyOptions.map((buyOption) => {
                            const stickers = buyOption.labels
                                .map((label) => getBuyOptionStickerType(label))
                                .filter((sticker) => sticker !== undefined);

                            return (
                                <BuyOptionCard
                                    key={`price-comparison-buy-option-${buyOption.link_id}`}
                                    variant={"radio"}
                                    id={buyOption.link_id.toString()}
                                    name="price-compare-items"
                                    value={buyOption.link_id.toString()}
                                    checked={selectedBuyOption.link_id === buyOption.link_id}
                                    buyOption={buyOption}
                                    price={getBuyOptionDisplayPrice(buyOption, selectedSizeOption)}
                                    parentArea="price_comparison"
                                    onChange={() => onItemSelect(buyOption)}
                                    shippingInformation={getShippingInformation(buyOption)}
                                    promotion={getPromotion(product, buyOption)}
                                    stickers={stickers}
                                    retailerLogoURL={buyOption.retailer_image}
                                    isCheckout={isCheckout}
                                />
                            );
                        })}
                    </RadioCardGroup>
                </div>
            </div>
            <div className={styles.buttonsWrapper}>
                <Button title={gettext("general.select")} onClick={selectBuyOption} width="full" />
                {shouldShowViewSellerDetails && (
                    <div data-testid="price-comparison-cards-view-seller-details">
                        <Button
                            title={gettext("product.view_seller_details")}
                            onClick={() => handleOpenRetailerModal(selectedBuyOption.retailer_slug)}
                            width="full"
                            variant="secondary"
                        />
                    </div>
                )}
            </div>
        </form>
    );
}

function PriceComparisonCardsDesktop({
    product,
    buyOptions,
}: PriceComparisonCardsProps): JSX.Element {
    const {
        productPurchaseType,
        activeProduct: { selectedSizeOption },
    } = useInStockProductPageContext();
    const isCheckout = productPurchaseType === ProductPurchaseType.CHECKOUT;

    // If more then 5 buyOptions we only show the first 5
    const displayedBuyOptionsLimit = 5;
    const [displayedBuyOptions, setDisplayedBuyOptions] = useState(
        buyOptions.slice(0, displayedBuyOptionsLimit)
    );

    // count of the not displayed buyOptions
    const [notDisplayedBuyOptionsCount, setNotDisplayedBuyOptionsCount] = useState(
        buyOptions.length - displayedBuyOptionsLimit
    );

    useEffect(() => {
        // Ignore if the buy options did not change
        if (
            JSON.stringify(buyOptions.map((buyOption) => buyOption.link_id)) ===
            JSON.stringify(displayedBuyOptions.map((buyOption) => buyOption.link_id))
        ) {
            return;
        }

        setDisplayedBuyOptions(buyOptions.slice(0, displayedBuyOptionsLimit));
        setNotDisplayedBuyOptionsCount(buyOptions.length - displayedBuyOptionsLimit);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buyOptions]);

    function onClickSeeMorePrices(): void {
        setDisplayedBuyOptions(buyOptions);
        analytics.event({
            category: "see-more-prices",
            action: "clicked",
            label: "pdp",
        });
    }

    const seeMorePriceSingular = gettext(
        "in_stock_product.compare_prices.see_more_prices_cta_singular"
    );
    const seeMorePricePlural = gettext("in_stock_product.compare_prices.see_more_prices_cta");

    const onBuyButtonClick = useCallback(
        (buyOption: ProductBuyOptionsSerializer, index: number) => {
            analytics.event({
                category: "buy_area",
                action: "clicked",
                label: "price_comparison",
                customData: {
                    buyOptionId: buyOption.link_id.toString(),
                    buyOptionIndex: index,
                },
            });
        },
        []
    );

    const onRetailerClick = useCallback((buyOption: ProductBuyOptionsSerializer, index: number) => {
        analytics.event({
            category: "retailer_store",
            action: "clicked",
            label: "price_comparison",
            customData: {
                retailerNetwork: buyOption.retailer_network,
                buyOptionId: buyOption.link_id.toString(),
                buyOptionIndex: index,
            },
        });
    }, []);

    return (
        <div className={styles.desktopWrapper}>
            <Text as="p" textStyle={"body-s"}>
                {gettext("price_comparison.price_options")}
            </Text>
            <div className={styles.desktopCards}>
                {displayedBuyOptions.map((buyOption, index) => {
                    const stickers = buyOption.labels
                        .map((label) => getBuyOptionStickerType(label))
                        .filter((sticker) => sticker !== undefined);

                    const cardProps = {
                        key: `price-comparison-buy-option-${buyOption.link_id}`,
                        variant: "wide" as const,
                        buyOption: buyOption,
                        price: getBuyOptionDisplayPrice(buyOption, selectedSizeOption),
                        parentArea: "price_comparison" as const,
                        shippingInformation: getShippingInformation(buyOption),
                        promotion: getPromotion(product, buyOption),
                        stickers: stickers,
                        comparePricesCount: displayedBuyOptions.length,
                        retailerLogoURL: buyOption.retailer_image,
                        onBuyButtonClick: () => onBuyButtonClick(buyOption, index),
                        onRetailerClick: () => onRetailerClick(buyOption, index),
                    };

                    return isCheckout ? (
                        <BuyOptionCard {...cardProps} isCheckout buyOptionIndex={index} />
                    ) : (
                        <BuyOptionCard {...cardProps} />
                    );
                })}
            </div>
            {displayedBuyOptions.length < buyOptions.length && (
                <div className={styles.seeMoreButton} onClick={onClickSeeMorePrices}>
                    <Text textStyle="body-s" underline>
                        {ngettext(
                            seeMorePriceSingular,
                            seeMorePricePlural,
                            notDisplayedBuyOptionsCount,
                            {
                                count: notDisplayedBuyOptionsCount,
                            }
                        )}
                    </Text>
                </div>
            )}
        </div>
    );
}

export function PriceComparisonCards({
    product,
    buyOptions,
}: PriceComparisonCardsProps): JSX.Element {
    const { isMobileViewport } = useDomViewport();

    return isMobileViewport ? (
        <PriceComparisonCardsMobile product={product} buyOptions={buyOptions} />
    ) : (
        <PriceComparisonCardsDesktop product={product} buyOptions={buyOptions} />
    );
}
